import { ChevronDown, Search } from "lucide-react";
import type {
  InputHTMLAttributes,
  LabelHTMLAttributes,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import { cn } from "../lib/cn";
import { Icon } from "./icon";

/**
 * Meridian's controls (docs/design/meridian.md, Field): 36 px, a `border-strong` line, and with `aria-invalid` a
 * `problem` border and a 3 px `problem` ring. Focus is the shared ring of the base layer.
 */
const control =
  "w-full rounded-(--radius-control) border border-border-strong bg-surface text-body text-ink transition-colors duration-(--duration-fast) placeholder:text-ink-muted disabled:cursor-not-allowed disabled:bg-muted disabled:text-ink-muted aria-invalid:border-danger aria-invalid:ring-3 aria-invalid:ring-tone-problem-bg";

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cn(control, "h-9 px-3", className)} {...props} />;
}

export function Textarea({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cn(control, "min-h-24 px-3 py-2", className)} {...props} />;
}

/** A native select with Meridian's chevron; `className` sizes the whole control. */
export function Select({ className, children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <div className={cn("relative", className)}>
      <select className={cn(control, "h-9 appearance-none ps-3 pe-9")} {...props}>
        {children}
      </select>
      <Icon
        icon={ChevronDown}
        className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 text-ink-muted"
      />
    </div>
  );
}

/**
 * Search: a `muted` fill, the icon on the left, and the shortcut that opens the command menu on the right when there
 * is one (`shortcut`, for example "⌘K"). The label is the caller's, visible or `sr-only`.
 */
export function SearchInput({
  className,
  shortcut,
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & { readonly shortcut?: string }) {
  return (
    <div
      className={cn(
        "flex h-9 items-center gap-2 rounded-(--radius-control) border border-transparent bg-muted px-2.5 transition-colors duration-(--duration-fast) focus-within:border-border-strong focus-within:bg-surface hover:border-border",
        className,
      )}
    >
      <Icon icon={Search} className="shrink-0 text-ink-muted" />
      <input
        type="search"
        className="min-w-0 flex-1 bg-transparent text-body text-ink outline-none placeholder:text-ink-muted"
        {...props}
      />
      {shortcut !== undefined && (
        <kbd className="rounded-[4px] border border-border bg-canvas px-1.5 font-sans text-tag text-ink-muted">
          {shortcut}
        </kbd>
      )}
    </div>
  );
}

export function Label({ className, ...props }: LabelHTMLAttributes<HTMLLabelElement>) {
  // biome-ignore lint/a11y/noLabelWithoutControl: callers pass htmlFor or nest the control.
  return <label className={cn("text-small font-medium text-ink-body", className)} {...props} />;
}
